/**
 * =============================================================
 * AADHAV ACADEMY – ADMIN SESSION INFO & ATTENDANCE API
 * Role: Admin
 * Scope: Tenant-based Individual Session Instance & Attendance Management
 * Standard: Enterprise / MNC Level
 * =============================================================
 */

import { apiCall } from "../core/apiCall";

/* =============================================================
   BASE ENDPOINT
============================================================= */

const ADMIN_SESSION_INFO_ENDPOINT =
  "/admin/sessions/session_info.php";

/* =============================================================
   GET – Fetch Session Info Instances (Paginated / Searchable)
   Handles nested child hydration arrays for attendance matrices
============================================================= */

export const getAdminSessionInfos = async (
  { page = 1, limit = 10, search = "" } = {},
  options = {}
) => {
  try {
    const res = await apiCall(
      "GET",
      ADMIN_SESSION_INFO_ENDPOINT,
      null,
      "Admin - Fetch Session Instances",
      {
        params: { page, limit, search },
        ...options,
      }
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message || "Failed to fetch session instances",
        data: [],
        meta: {},
      };
    }

    return {
      success: true,
      data: res.data || [],
      meta: res.meta || {
        total: 0,
        page: 1,
        limit,
        total_pages: 0,
      },
    };
  } catch (error) {
    console.error(
      "Admin Fetch Session Instances Error:",
      error
    );

    return {
      success: false,
      message:
        error.message || "Unexpected error occurred",
      data: [],
      meta: {},
    };
  }
};

/* =============================================================
   GET – Fetch Single Session Instance by ID
   Returns metadata and complete `attendance_roster` state array
============================================================= */

export const getAdminSessionInfoById = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Instance ID",
      };
    }

    const res = await apiCall(
      "GET",
      `${ADMIN_SESSION_INFO_ENDPOINT}?id=${id}`,
      null,
      "Admin - Fetch Session Instance By ID",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to fetch session instance details",
      };
    }

    return {
      success: true,
      data: res.data,
    };
  } catch (error) {
    console.error(
      "Admin Fetch Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   POST – Create Session Instance
   Payload automatically handles instructor fallback & auto-rosters
============================================================= */

export const createAdminSessionInfo = async (
  payload,
  options = {}
) => {
  try {
    const res = await apiCall(
      "POST",
      ADMIN_SESSION_INFO_ENDPOINT,
      payload,
      "Admin - Create Session Instance",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to create session instance",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message:
        res.message ||
        "Session instance created and roster initialized successfully",
    };
  } catch (error) {
    console.error(
      "Admin Create Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   PUT – Update Session Instance & Custom Attendance Variations
   Expects payload to contain custom `attendance_roster` tracking
============================================================= */

export const updateAdminSessionInfo = async (
  id,
  payload,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Instance ID",
      };
    }

    const res = await apiCall(
      "PUT",
      `${ADMIN_SESSION_INFO_ENDPOINT}?id=${id}`,
      payload,
      "Admin - Update Session Instance & Roster",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to update session instance details",
      };
    }

    return {
      success: true,
      data: res.data || null,
      message:
        res.message ||
        "Session instance and roster variations updated successfully",
    };
  } catch (error) {
    console.error(
      "Admin Update Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};

/* =============================================================
   DELETE – Cascade Delete Session Instance
   Purges both the session_info entry and linked student attendance tracking
============================================================= */

export const deleteAdminSessionInfo = async (
  id,
  options = {}
) => {
  try {
    if (!id) {
      return {
        success: false,
        message: "Invalid Session Instance ID",
      };
    }

    const res = await apiCall(
      "DELETE",
      `${ADMIN_SESSION_INFO_ENDPOINT}?id=${id}`,
      null,
      "Admin - Delete Session Instance",
      options
    );

    if (!res || res.status !== true) {
      return {
        success: false,
        message:
          res?.message ||
          "Failed to delete session instance tracking asset",
      };
    }

    return {
      success: true,
      message:
        res.message ||
        "Session instance tracking architecture removed safely",
    };
  } catch (error) {
    console.error(
      "Admin Delete Session Instance Error:",
      error
    );

    return {
      success: false,
      message:
        error.message ||
        "Unexpected error occurred",
    };
  }
};